import { Box, Palette, useTheme } from '@mui/material';
import { Ban, CheckCircle, Search } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';

import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';
import Paper from 'components/paper';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import SelfServiceTabsHeader from '../../components/self-service-tabs-header';

import { usePagination } from 'hooks/use-pagination';
import { loadState, saveState } from 'utils/storage';
import { AeratorTypes } from 'views/self-service/features/aerators/types/aerator-type.enum';
import { Aerator } from 'views/self-service/features/aerators/types/aerators.type';
import { AeratorsProvider, useAeratorsState } from './context/aerators-context';

import CreateAerator from 'views/self-service/features/aerators/components/create-aerator';
import UpdateAerator from 'views/self-service/features/aerators/components/update-aerator';

type AeratorsProps = {};

const getIcon = (status: boolean, color: Palette) => {
  const Icon = status ? Ban : CheckCircle;
  return <Icon color={status ? color.darkBlue.o25 : color.bluViu.main} size={20} strokeWidth={3} />;
};

type Filters = {
  searchValue: string;
  showActiveAerators: boolean;
  showInactiveAerators: boolean;
};

const defaultValues: Filters = {
  searchValue: '',
  showActiveAerators: true,
  showInactiveAerators: false,
};

const PAGINATION_LIMIT = 7;
const PAGE_NAME_ID = 'selfService.aerators';

const Aerators: FC<AeratorsProps> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { isLoading, aerators } = useAeratorsState();

  // #region STATE
  const [filters, setFilters] = useState<Filters>(loadState({ key: 'aeratorFilters' }) || defaultValues);
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  // #endregion STATE

  // #region OPTIONS
  const aeratorsListOptions: HeadCell[] = [
    { id: 'name', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
    { id: 'brand', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.brand` }) },
    { id: 'type', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.type` }) },
    { id: 'horsepower', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.horsepower` }) },
    { id: 'biomassSupported', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.biomassSupported` }) },
    { id: 'hidden', align: 'center', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.active` }) },
    {
      id: 'actions',
      label: '',
      autoWidth: true,
    },
  ];
  // #endregion OPTIONS

  // #region HANDLERS
  const resetStates = () => {
    updateFilters(defaultValues);
  };

  const updateFilters = (newFilters: Filters) => {
    setFilters(newFilters);
    saveState({ key: 'aeratorFilters', state: newFilters });
  };
  // #endregion HANDLRES

  // #region RENDERS
  const aeratorsList = useMemo(() => {
    let rows: Aerator[] | undefined = aerators;
    rows = _.chain(aerators)
      .filter((aerator) => (filters.showActiveAerators ? true : aerator.hidden))
      .filter((aerator) => (filters.showInactiveAerators ? true : !aerator.hidden))
      .filter((aerator) => aerator.name.toLowerCase().includes(filters.searchValue.toLowerCase()))
      .sortBy((aerator) => aerator.name.toLowerCase())
      .value();

    return rows;
  }, [filters, aerators]);
  // #endregion RENDERS

  return (
    <>
      <SelfServiceTabsHeader utils={<Breadcrumbs />} />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <CreateAerator />
      </Box>
      <Paper transparent>
        <FilterSidebar
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          showClearFilter={!!filters.searchValue || !filters.showActiveAerators || filters.showInactiveAerators}
          clearFilters={() => {
            resetStates();
          }}>
          <Box display={'flex'} flexDirection={'row'} gap={4}>
            <Switch
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showActive` })}
              value={filters.showActiveAerators}
              onChange={() => {
                handlePagination(1);
                let newFilters = { ...filters, showActiveAerators: !filters.showActiveAerators };
                updateFilters(newFilters);
              }}
            />
            <Switch
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showInactive` })}
              value={filters.showInactiveAerators}
              onChange={() => {
                handlePagination(1);
                let newFilters = { ...filters, showInactiveAerators: !filters.showInactiveAerators };
                updateFilters(newFilters);
              }}
            />
          </Box>
          <TextField
            value={filters.searchValue}
            label={intl.formatMessage({ id: 'general.search' })}
            onChange={(event) => {
              handlePagination(1);
              let newFilters = { ...filters, searchValue: event.target.value as string };
              updateFilters(newFilters);
            }}
            endAdornment={<Search size={18} />}
          />
        </FilterSidebar>
        <List
          isLoading={isLoading}
          noResult={{
            show: !aeratorsList.length,
            text: intl.formatMessage({ id: 'general.noResults' }),
          }}
          options={aeratorsListOptions}>
          {aeratorsList.slice(from, to).map((aerator, idx) => (
            <ListRow key={aerator.id}>
              <ListCell align="left">{aerator.name}</ListCell>
              <ListCell align="left" capitalize>
                {aerator.brand}
              </ListCell>
              <ListCell align="left" capitalize>
                <FormattedMessage id={`${PAGE_NAME_ID}.types.${AeratorTypes.find((st) => st.id === aerator.type)?.name}`} />
              </ListCell>
              <ListCell align="left">{aerator.horsepower}</ListCell>
              <ListCell align="left">{aerator.biomassSupported}</ListCell>
              <ListCell align="center">
                {getIcon(aerator.hidden, theme.palette)}
              </ListCell>
              <ListCell>
                <UpdateAerator idx={idx} aerator={aerator} />
              </ListCell>
            </ListRow>
          ))}
        </List>
        {aeratorsList.length > PAGINATION_LIMIT && (
          <Pagination currentPage={currentPage} pages={Math.ceil(aeratorsList.length / PAGINATION_LIMIT)} onClick={handlePagination} />
        )}
      </Paper>
    </>
  );
};

const AeratorsContainer: React.FC<{}> = ({}) => {
  return (
    <AeratorsProvider>
      <Aerators />
    </AeratorsProvider>
  );
};

export default AeratorsContainer;
